import { useState } from "react";
import { Loader2, Calendar as CalIcon } from "lucide-react";
import { RSVP } from "@/constants/testIds";

const PillOption = ({ active, onClick, children, testId, name }) => (
  <button
    type="button"
    role="radio"
    aria-checked={active}
    onClick={onClick}
    data-testid={testId}
    data-name={name}
    className={[
      "flex-1 px-5 py-3.5 border text-sm tracking-wide font-body transition-all duration-200",
      "hover:border-gold hover:bg-gold/[0.06]",
      active
        ? "border-gold bg-gold/[0.1] text-navy"
        : "border-edge text-navy/70 bg-transparent",
    ].join(" ")}
  >
    {children}
  </button>
);

export default function RsvpForm({ onSubmit, loading, onAddToCalendar }) {
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [attending, setAttending] = useState(""); // "yes" | "no"
  const [plusOne, setPlusOne] = useState(""); // "yes" | "no"
  const [errors, setErrors] = useState({});

  const validate = () => {
    const e = {};
    if (!name.trim()) e.name = "Please share your name.";
    if (!phone.trim()) e.phone = "Please share a phone number.";
    else if (!/^[\d\s()+\-]{5,30}$/.test(phone.trim()))
      e.phone = "That phone number looks off.";
    if (!attending) e.attending = "Let us know if you can make it.";
    if (attending === "yes" && !plusOne)
      e.plusOne = "Will you be bringing a +1?";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    onSubmit({
      name: name.trim(),
      phone: phone.trim(),
      attending,
      plus_one: attending === "yes" ? plusOne : "no",
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-8" noValidate>
      {/* Name */}
      <div>
        <label htmlFor="rsvp-name" className="eyebrow text-navy/60">
          Your name
        </label>
        <input
          id="rsvp-name"
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Full name"
          className="editorial-input mt-2"
          data-testid={RSVP.nameInput}
          autoComplete="name"
        />
        {errors.name && (
          <p className="mt-2 text-xs text-golddeep">{errors.name}</p>
        )}
      </div>

      {/* Phone */}
      <div>
        <label htmlFor="rsvp-phone" className="eyebrow text-navy/60">
          Phone number
        </label>
        <input
          id="rsvp-phone"
          type="tel"
          inputMode="tel"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          placeholder="+91 98 ..."
          className="editorial-input mt-2"
          data-testid={RSVP.phoneInput}
          autoComplete="tel"
        />
        {errors.phone && (
          <p className="mt-2 text-xs text-golddeep">{errors.phone}</p>
        )}
      </div>

      {/* Attending */}
      <div>
        <span className="eyebrow text-navy/60 block mb-3">
          Will you attend?
        </span>
        <div className="flex gap-3" role="radiogroup" aria-label="Attending">
          <PillOption
            active={attending === "yes"}
            onClick={() => setAttending("yes")}
            testId={RSVP.attendingYes}
            name="attending"
          >
            Joyfully, yes
          </PillOption>
          <PillOption
            active={attending === "no"}
            onClick={() => {
              setAttending("no");
              setPlusOne("");
            }}
            testId={RSVP.attendingNo}
            name="attending"
          >
            Regretfully, no
          </PillOption>
        </div>
        {errors.attending && (
          <p className="mt-2 text-xs text-golddeep">{errors.attending}</p>
        )}
      </div>

      {/* Plus one — only if attending */}
      {attending === "yes" && (
        <div className="animate-in fade-in duration-300">
          <span className="eyebrow text-navy/60 block mb-3">
            Will you bring a +1?
          </span>
          <div className="flex gap-3" role="radiogroup" aria-label="Plus one">
            <PillOption
              active={plusOne === "yes"}
              onClick={() => setPlusOne("yes")}
              testId={RSVP.plusOneYes}
              name="plus_one"
            >
              Yes, +1
            </PillOption>
            <PillOption
              active={plusOne === "no"}
              onClick={() => setPlusOne("no")}
              testId={RSVP.plusOneNo}
              name="plus_one"
            >
              No, just me
            </PillOption>
          </div>
          {errors.plusOne && (
            <p className="mt-2 text-xs text-golddeep">{errors.plusOne}</p>
          )}
        </div>
      )}

      <button
        type="submit"
        disabled={loading}
        data-testid={RSVP.submitButton}
        className="w-full bg-navy text-cream py-4 text-sm uppercase tracking-[0.25em] font-body hover:bg-gold hover:text-navy transition-all duration-300 hover:scale-[1.01] disabled:opacity-60 disabled:hover:bg-navy disabled:hover:text-cream disabled:hover:scale-100 flex items-center justify-center gap-3"
      >
        {loading ? (
          <>
            <Loader2 className="w-4 h-4 animate-spin" /> Sending
          </>
        ) : (
          "Send RSVP"
        )}
      </button>

      {onAddToCalendar && (
        <button
          type="button"
          onClick={onAddToCalendar}
          data-testid="add-to-calendar-button"
          className="w-full mt-3 border border-edge text-navy py-3.5 text-sm uppercase tracking-[0.25em] font-body hover:border-gold hover:text-golddeep transition-all duration-300 flex items-center justify-center gap-3"
        >
          <CalIcon className="w-4 h-4" strokeWidth={1.6} /> Add to calendar
        </button>
      )}
    </form>
  );
}
